import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";

import { addAccessTokenInterceptor } from "../services/apiClient";
import { invitationLoginOptions } from "../services/invitationParams";

/**
 * Installs the bearer-token interceptor once, above the router, and renders
 * nothing until it is in place.
 *
 * It used to be installed from a `useEffect` inside InsightsLayout, which had two
 * consequences worth stating because both are easy to reintroduce:
 *
 *   1. Any route NOT nested under that layout sent no Authorization header at all.
 *      The admin pages are the first such routes, so nothing there could have
 *      worked without moving this.
 *   2. The effect added an interceptor and never removed one, so each run stacked
 *      another. Invisible, because they all set the same header from the same
 *      cached token -- but each one awaits `getAccessTokenSilently` in turn.
 *
 * **Why children are gated rather than rendered straight away.** React runs
 * effects child-first, so a child's own mount effect fires BEFORE this one. When
 * this component rendered its children immediately, every mount-time request of
 * a page load -- `InsightsLayout`'s tenant and sensor-group fetches, the viewer
 * query -- went out before the interceptor existed, and therefore with no
 * `Authorization` header at all. Moving the install up from InsightsLayout fixed
 * routes outside that layout and broke the first request of every route inside
 * it, which is strictly worse.
 *
 * That failure was invisible from the browser: the API Gateway JWT authorizer
 * rejects an unauthenticated request before the Lambda runs, and that API has no
 * CORS configuration, so its 401 carries no `Access-Control-Allow-Origin`.
 * Chrome therefore reported "blocked by CORS policy" and never showed the 401 to
 * JavaScript -- which also means the response interceptor's
 * `401 -> loginWithRedirect` could not fire. An expired token presented as an
 * empty dashboard and a console blaming CORS.
 *
 * Returning null for one render is what makes the ordering safe: children cannot
 * mount, and so cannot fetch, until the effect below has run. The cost is a
 * single extra render of the shell, which sits behind Auth0's own loading state
 * anyway. `AuthedApiProvider.test.tsx` pins the ordering; it fails with
 * `installed` removed.
 */
export default function AuthedApiProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // A 401 means the token is missing or expired, which signing in again fixes.
    // A 403 is a fact about this viewer's permissions and is left to the call
    // site, which knows what its own refusal should look like -- bouncing the
    // viewer to Auth0 over a 403 would loop them straight back to the same
    // refusal.
    const remove = addAccessTokenInterceptor(getAccessTokenSilently, {
      onUnauthenticated: () => {
        // Carries an invitation too. A token that expires mid-acceptance would
        // otherwise send the person back through a plain login and quietly turn
        // their acceptance into an ordinary sign-in.
        loginWithRedirect(invitationLoginOptions());
      },
    });

    setInstalled(true);

    return remove;
  }, [getAccessTokenSilently, loginWithRedirect]);

  // Deliberately not a spinner. The interceptor is installed in the very next
  // effect, so this is one frame; a spinner would flash.
  if (!installed) {
    return null;
  }

  return <>{children}</>;
}
